import React, { useState, useEffect, useRef } from 'react'
import './ContentManagement.css'
import { CustomButton } from '../../../common/ReusableComponent/CustomButton'
import { FaEdit, FaTimes, FaTrash } from 'react-icons/fa'
import LoadingSpinner from '../Orders/LoadingSpinner'
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { useToastHandler } from '../../../common/ToastHandler'
import SuccessModal from '../Reusable/SuccessModal'
import { useStore } from '../../../../context/StoreContext'
import globalConfig from '../../../../config/Global.json'
import OfferLine from './OfferLine'
import useSwal from '../../../common/ReusableComponent/useSwal'

const ContentManagement = () => {
    const { currentStoreId } = useStore()
    const { confirm } = useSwal()
    const { handleToast } = useToastHandler()
    const [activeTab, setActiveTab] = useState('header')
    const [storeId, setStoreId] = useState(null)
    const [content, setContent] = useState({
        logo: '',
        siteName: '',
        menuItems: [],
        heroTitle: '',
        heroSubtitle: '',
        heroImage1: '',
        heroImage2: '',
        heroImage3: '',
        heroImage4: '',
        companyName: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        copyrightText: '',
        socialLinks: [],
        aboutTitle: '',
        aboutDescription: '',
        aboutImage: '',
        contactNumber: '',
        contactEmail: '',
        contactAddress: ''
    })
    const [editing, setEditing] = useState(null)
    const [logoFile, setLogoFile] = useState(null)
    const [heroImageFiles, setHeroImageFiles] = useState({ img1: null, img2: null, img3: null, img4: null })
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [errors, setErrors] = useState({})
    const [headerId, setHeaderId] = useState(null)
    const [footerId, setFooterId] = useState(null)
    const [heroId, setHeroId] = useState(null)
    const [aboutId, setAboutId] = useState(null)
    const [contactId, setContactId] = useState(null)
    const [loading, setLoading] = useState(false)
    const [hasChanges, setHasChanges] = useState(false)
    const [originalContent, setOriginalContent] = useState({})
    const { Get, Post, Patch, Delete, getAPI, PostFormData, PatchFormData } = useApi()
    const loadedRef = useRef({ header: false, footer: false, hero: false, about: false, contact: false })

    const buildMediaUrl = (value) => {
        if (!value) return ''

        const raw = String(value)
        const gatewayHost = globalConfig.api.host.replace(/\/$/, '')

        try {
            const parsed = new URL(raw)
            if (parsed.pathname.includes('/media/')) {
                return `${gatewayHost}${parsed.pathname}`
            }
            return raw
        } catch (error) {
            if (raw.startsWith('/media/')) {
                return `${gatewayHost}${raw}`
            }
            if (raw.startsWith('media/')) {
                return `${gatewayHost}/${raw}`
            }
            if (raw.startsWith('/')) {
                return `${gatewayHost}${raw}`
            }
            return raw
        }
    }

    useEffect(() => {
        const fetchUserStore = async () => {
            try {
                const response = await Get('getAllStoresOfUser')
                console.log('🏪 getAllStoresOfUser response:', response)
                console.log('🏪 first_store:', response?.first_store)
                console.log('🏪 first_store.id:', response?.first_store?.id)
                console.log('🏪 currentStoreId from context:', currentStoreId)

                // Use currentStoreId from context if available, otherwise use first_store
                const selectedStoreId = currentStoreId || response?.first_store?.id
                console.log('🏪 Selected Store ID:', selectedStoreId)

                if (selectedStoreId) {
                    setStoreId(selectedStoreId)
                }
            } catch (error) {
                console.error('Error fetching user store:', error)
            }
        }
        fetchUserStore()
    }, [currentStoreId])

    // Track changes
    useEffect(() => {
        const hasContentChanged = JSON.stringify(content) !== JSON.stringify(originalContent)
        setHasChanges(hasContentChanged || logoFile || Object.values(heroImageFiles).some(file => file))
    }, [content, originalContent, logoFile, heroImageFiles])

    useEffect(() => {
        if (!storeId) return

        if (activeTab === 'header') {
            fetchHeader()
        } else if (activeTab === 'footer') {
            fetchFooter()
        } else if (activeTab === 'hero') {
            fetchHero()
        } else if (activeTab === 'Aboutus') {
            fetchAbout()
        } else if (activeTab === 'Contactus') {
            fetchContact()
        } else if (activeTab === 'offerline') {
            // OfferLine component handles its own data fetching
        }
    }, [activeTab, storeId])

    const fetchHeader = async () => {
        if (!storeId) {
            console.warn('No store ID available')
            setLoading(false)
            return
        }
        setLoading(true)
        try {
            const domain = window.location.hostname
            const apiUrl = `${getAPI('headers')}?store_id=${storeId}`
            const result = await Get(`${apiUrl}&domain=${domain}`)
            if (result) {
                // CRITICAL: Only use data that matches the requested store
                const data = Array.isArray(result)
                    ? result.find(item => item.store === storeId)
                    : (result.store === storeId ? result : null)

                if (!data) {
                    console.error('❌ STORE MISMATCH: No header data found for store:', storeId)
                    console.error('Received data for stores:', Array.isArray(result) ? result.map(r => r.store) : result.store)
                    handleToast({ type: 'ERROR', message: 'No header data found for this store' })
                    setLoading(false)
                    return
                }

                const logoUrl = buildMediaUrl(data?.logo)
                let menuItems = []
                if (data?.menu_items) {
                    try {
                        menuItems = typeof data.menu_items === 'string' ? JSON.parse(data.menu_items) : data.menu_items
                    } catch (e) {
                        menuItems = []
                    }
                }
                setHeaderId(data?.id)
                setContent(prev => ({
                    ...prev,
                    logo: logoUrl,
                    siteName: data?.title || '',
                    menuItems: Array.isArray(menuItems) ? menuItems : []
                }))
            }
        } catch (error) {
            console.error('Error fetching header:', error)
        }
        setLoading(false)
    }

    const fetchFooter = async () => {
        if (!storeId) {
            console.warn('No store ID available')
            setLoading(false)
            return
        }
        setLoading(true)
        try {
            const domain = window.location.hostname
            const apiUrl = `${getAPI('footer')}?store_id=${storeId}`
            const result = await Get(`${apiUrl}&domain=${domain}`)
            if (result) {
                // CRITICAL: Only use data that matches the requested store
                const data = Array.isArray(result)
                    ? result.find(item => item.store === storeId)
                    : (result.store === storeId ? result : null)

                if (!data) {
                    console.error('❌ STORE MISMATCH: No footer data found for store:', storeId)
                    setLoading(false)
                    return
                }

                let socialLinks = []
                if (data?.social_links) {
                    try {
                        socialLinks = typeof data.social_links === 'string' ? JSON.parse(data.social_links) : data.social_links
                    } catch (e) {
                        socialLinks = []
                    }
                }
                setFooterId(data?.id)
                setContent(prev => ({
                    ...prev,
                    companyName: data?.company_name || '',
                    description: data?.description || '',
                    address: data?.address || '',
                    phone: data?.phone || '',
                    email: data?.email || '',
                    copyrightText: data?.copyright_text || '',
                    socialLinks: Array.isArray(socialLinks) ? socialLinks : []
                }))
            }
        } catch (error) {
            console.error('Error fetching footer:', error)
        }
        setLoading(false)
    }

    const fetchHero = async () => {
        if (!storeId) {
            console.warn('No store ID available')
            setLoading(false)
            return
        }
        setLoading(true)
        try {
            const domain = window.location.hostname
            const apiUrl = `${getAPI('hero')}?store_id=${storeId}`
            console.log('🔍 Fetching Hero - Store ID:', storeId)
            console.log('🔍 Fetching Hero - API URL:', apiUrl)
            const result = await Get(`${apiUrl}&domain=${domain}`)
            console.log('🔍 Hero API Raw Response:', result)
            console.log('🔍 Is Array?:', Array.isArray(result))

            if (result) {
                const data = Array.isArray(result)
                    ? result.find(item => item.store === storeId)
                    : (result.store === storeId ? result : null)

                console.log('🔍 Found matching data:', data)
                console.log('🔍 Data store value:', data?.store)
                console.log('🔍 StoreId value:', storeId)
                console.log('🔍 Match?:', data?.store === storeId)

                if (!data) {
                    console.error('❌ STORE MISMATCH: No hero data found for store:', storeId)
                    if (Array.isArray(result)) {
                        console.error('Available stores in response:', result.map(r => r.store))
                    }
                    setLoading(false)
                    return
                }

                console.log('✅ Hero data matched successfully:', data)

                setHeroId(data?.id)
                setContent(prev => ({
                    ...prev,
                    heroTitle: data?.title || '',
                    heroSubtitle: data?.subtitle || '',
                    heroImage1: buildMediaUrl(data?.image1),
                    heroImage2: buildMediaUrl(data?.image2),
                    heroImage3: buildMediaUrl(data?.image3),
                    heroImage4: buildMediaUrl(data?.image4)
                }))
                console.log('✅ Hero content updated in state')
            }
        } catch (error) {
            console.error('Error fetching hero:', error)
        }
        setLoading(false)
    }

    const fetchAbout = async () => {
        if (!storeId) {
            console.warn('No store ID available')
            setLoading(false)
            return
        }
        setLoading(true)
        try {
            const domain = window.location.hostname
            const apiUrl = `${getAPI('about')}?store_id=${storeId}`
            const result = await Get(`${apiUrl}&domain=${domain}`)
            if (result) {
                // CRITICAL: Only use data that matches the requested store
                const data = Array.isArray(result)
                    ? result.find(item => item.store === storeId)
                    : (result.store === storeId ? result : null)

                if (!data) {
                    console.error('❌ STORE MISMATCH: No about data found for store:', storeId)
                    setLoading(false)
                    return
                }

                const imageUrl = buildMediaUrl(data?.image)
                setAboutId(data?.id)
                setContent(prev => ({
                    ...prev,
                    aboutTitle: data?.title || '',
                    aboutDescription: data?.description || '',
                    aboutImage: imageUrl
                }))
            }
        } catch (error) {
            console.error('Error fetching about:', error)
        }
        setLoading(false)
    }

    const fetchContact = async () => {
        if (!storeId) {
            console.warn('No store ID available')
            setLoading(false)
            return
        }
        setLoading(true)
        try {
            const domain = window.location.hostname
            const apiUrl = `${getAPI('contact')}?store_id=${storeId}`
            const result = await Get(`${apiUrl}&domain=${domain}`)
            if (result) {
                // CRITICAL: Only use data that matches the requested store
                const data = Array.isArray(result)
                    ? result.find(item => item.store === storeId)
                    : (result.store === storeId ? result : null)

                if (!data) {
                    console.error('❌ STORE MISMATCH: No contact data found for store:', storeId)
                    setLoading(false)
                    return
                }

                setContactId(data?.id)
                setContent(prev => ({
                    ...prev,
                    contactNumber: data?.number || '',
                    contactEmail: data?.email || '',
                    contactAddress: data?.address || ''
                }))
            }
        } catch (error) {
            console.error('Error fetching contact:', error)
        }
        setLoading(false)
    }

    const handleSaveAll = async () => {
        if (!hasChanges) return

        setLoading(true)

        try {
            await handleSave()
            setHasChanges(false)
            setLogoFile(null)
            setHeroImageFiles({ img1: null, img2: null, img3: null, img4: null })
        } catch (error) {
            console.error('Error saving changes:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        console.log('Store ID:', storeId)

        const newErrors = {}

        if (activeTab === 'footer') {
            const phoneDigits = content.phone.replace(/\D/g, '')
            if (content.phone && phoneDigits.length < 10) {
                newErrors.phone = 'Phone number must be at least 10 digits'
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (content.email && !emailRegex.test(content.email)) {
                newErrors.email = 'Please enter a valid email address'
            }
        }

        if (activeTab === 'Contactus') {
            const phoneDigits = content.contactNumber.replace(/\D/g, '')
            if (content.contactNumber && phoneDigits.length < 10) {
                newErrors.contactNumber = 'Phone number must be at least 10 digits'
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (content.contactEmail && !emailRegex.test(content.contactEmail)) {
                newErrors.contactEmail = 'Please enter a valid email address'
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        setErrors({})
        setLoading(true)
        let result

        try {
            if (activeTab === 'header') {
                if (!storeId) {
                    handleToast({ type: 'ERROR', message: 'No store selected' })
                    return
                }
                const domain = window.location.hostname
                if (logoFile) {
                    const formData = new FormData()
                    console.log('Store ID being sent:', storeId, 'Type:', typeof storeId)
                    formData.append('title', content.siteName)
                    formData.append('logo', logoFile)
                    formData.append('menu_items', JSON.stringify(content.menuItems))
                    formData.append('store', storeId)
                    console.log('FormData entries:')
                    for (let pair of formData.entries()) {
                        console.log(pair[0], pair[1])
                    }
                    if (headerId) {
                        result = await PatchFormData('headers', `${headerId}?store_id=${storeId}&domain=${domain}`, formData)
                    } else {
                        const apiUrl = `${getAPI('headers')}?store_id=${storeId}&domain=${domain}`
                        result = await PostFormData(apiUrl, formData)
                    }
                } else if (headerId) {
                    result = await Patch('headers', `${headerId}?store_id=${storeId}&domain=${domain}`, {
                        title: content.siteName,
                        menu_items: content.menuItems,
                        store: storeId
                    })
                } else {
                    const formData = new FormData()
                    formData.append('title', content.siteName)
                    formData.append('menu_items', JSON.stringify(content.menuItems))
                    formData.append('store', storeId)
                    const apiUrl = `${getAPI('headers')}?store_id=${storeId}&domain=${domain}`
                    result = await PostFormData(apiUrl, formData)
                }
                if (result) {
                    setSuccessMessage('Header updated successfully')
                    setShowSuccessModal(true)
                    setLogoFile(null)
                    await fetchHeader()
                }
            } else if (activeTab === 'footer') {
                if (!storeId) {
                    handleToast({ type: 'ERROR', message: 'No store selected' })
                    return
                }
                const domain = window.location.hostname
                const payload = {
                    company_name: content.companyName,
                    description: content.description,
                    address: content.address,
                    phone: content.phone,
                    email: content.email,
                    copyright_text: content.copyrightText,
                    footer_links: [],
                    social_links: content.socialLinks,
                    store: storeId
                }
                if (footerId) {
                    result = await Patch('footer', `${footerId}?store_id=${storeId}&domain=${domain}`, payload)
                } else {
                    const apiUrl = `${getAPI('footer')}?store_id=${storeId}&domain=${domain}`
                    result = await Post(apiUrl, payload)
                }
                if (result) {
                    setSuccessMessage('Footer updated successfully')
                    setShowSuccessModal(true)
                    await fetchFooter()
                }
            } else if (activeTab === 'hero') {
                if (!storeId) {
                    handleToast({ type: 'ERROR', message: 'No store selected' })
                    return
                }
                const domain = window.location.hostname
                const hasImages = heroImageFiles.img1 || heroImageFiles.img2 || heroImageFiles.img3 || heroImageFiles.img4

                if (hasImages) {
                    const formData = new FormData()
                    formData.append('title', content.heroTitle)
                    formData.append('subtitle', content.heroSubtitle)
                    formData.append('button_text', 'Shop Now')
                    formData.append('button_link', '/products')
                    formData.append('store', storeId)

                    if (heroImageFiles.img1) formData.append('image1', heroImageFiles.img1)
                    if (heroImageFiles.img2) formData.append('image2', heroImageFiles.img2)
                    if (heroImageFiles.img3) formData.append('image3', heroImageFiles.img3)
                    if (heroImageFiles.img4) formData.append('image4', heroImageFiles.img4)

                    if (heroId) {
                        result = await PatchFormData('hero', `${heroId}?store_id=${storeId}&domain=${domain}`, formData)
                    } else {
                        const apiUrl = `${getAPI('hero')}?store_id=${storeId}&domain=${domain}`
                        result = await PostFormData(apiUrl, formData)
                    }
                } else if (heroId) {
                    result = await Patch('hero', `${heroId}?store_id=${storeId}&domain=${domain}`, {
                        title: content.heroTitle,
                        subtitle: content.heroSubtitle,
                        store: storeId
                    })
                } else {
                    const apiUrl = `${getAPI('hero')}?store_id=${storeId}&domain=${domain}`
                    result = await Post(apiUrl, {
                        title: content.heroTitle,
                        subtitle: content.heroSubtitle,
                        button_text: 'Shop Now',
                        button_link: '/products',
                        store: storeId
                    })
                }

                if (result) {
                    setSuccessMessage('Hero section updated successfully')
                    setShowSuccessModal(true)
                    setHeroImageFiles({ img1: null, img2: null, img3: null, img4: null })
                    await fetchHero()
                }
            } else if (activeTab === 'Aboutus') {
                if (!storeId) {
                    handleToast({ type: 'ERROR', message: 'No store selected' })
                    return
                }
                const domain = window.location.hostname
                if (logoFile) {
                    const formData = new FormData()
                    formData.append('title', content.aboutTitle)
                    formData.append('description', content.aboutDescription)
                    formData.append('image', logoFile)
                    formData.append('store', storeId)
                    if (aboutId) {
                        result = await PatchFormData('about', `${aboutId}?store_id=${storeId}&domain=${domain}`, formData)
                    } else {
                        const apiUrl = `${getAPI('about')}?store_id=${storeId}&domain=${domain}`
                        result = await PostFormData(apiUrl, formData)
                    }
                } else if (aboutId) {
                    result = await Patch('about', `${aboutId}?store_id=${storeId}&domain=${domain}`, {
                        title: content.aboutTitle,
                        description: content.aboutDescription,
                        store: storeId
                    })
                } else {
                    const apiUrl = `${getAPI('about')}?store_id=${storeId}&domain=${domain}`
                    result = await Post(apiUrl, {
                        title: content.aboutTitle,
                        description: content.aboutDescription,
                        store: storeId
                    })
                }
                if (result) {
                    setSuccessMessage('About Us updated successfully')
                    setShowSuccessModal(true)
                    setLogoFile(null)
                    await fetchAbout()
                }
            } else if (activeTab === 'Contactus') {
                if (!storeId) {
                    handleToast({ type: 'ERROR', message: 'No store selected' })
                    return
                }
                const domain = window.location.hostname
                if (contactId) {
                    result = await Patch('contact', `${contactId}?store_id=${storeId}&domain=${domain}`, {
                        number: content.contactNumber,
                        email: content.contactEmail,
                        address: content.contactAddress,
                        store: storeId
                    })
                } else {
                    const apiUrl = `${getAPI('contact')}?store_id=${storeId}&domain=${domain}`
                    result = await Post(apiUrl, {
                        number: content.contactNumber,
                        email: content.contactEmail,
                        address: content.contactAddress,
                        store: storeId
                    })
                }
                if (result) {
                    setSuccessMessage('Contact Us updated successfully')
                    setShowSuccessModal(true)
                    await fetchContact()
                }
            }
        } catch (error) {
            console.error('Error saving content:', error)
            console.error('Error response:', error.response?.data)
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        setEditing(null)
        setLogoFile(null)
        setHeroImageFiles({ img1: null, img2: null, img3: null, img4: null })
        setErrors({})
    }

    const addMenuItem = () => {
        setContent({ ...content, menuItems: [...content.menuItems, { name: '', link: '' }] })
    }

    const removeMenuItem = (index) => {
        setContent({ ...content, menuItems: content.menuItems.filter((_, i) => i !== index) })
    }

    const updateMenuItem = (index, field, value) => {
        const newItems = [...content.menuItems]
        newItems[index][field] = value
        setContent({ ...content, menuItems: newItems })
    }

    const addSocialLink = () => {
        setContent({ ...content, socialLinks: [...content.socialLinks, { platform: '', url: '' }] })
    }

    const removeSocialLink = (index) => {
        setContent({ ...content, socialLinks: content.socialLinks.filter((_, i) => i !== index) })
    }

    const updateSocialLink = (index, field, value) => {
        const newLinks = [...content.socialLinks]
        newLinks[index][field] = value
        setContent({ ...content, socialLinks: newLinks })
    }

    const handleDeleteImage = async (imageType, imageNumber) => {
        console.log('Delete called with:', { imageType, imageNumber })

        if (imageType === 'hero' && !heroId) return
        if (imageType === 'about' && !aboutId) return
        if (imageType === 'logo' && !headerId) return

        setLoading(true)

        try {
            const domain = window.location.hostname
            let result

            if (imageType === 'hero') {
                result = await Delete('hero', `${heroId}?store_id=${storeId}&image_field=image${imageNumber}&domain=${domain}`)
                if (result) {
                    setSuccessMessage(`Hero Image ${imageNumber} deleted successfully`)
                    setContent(prev => ({
                        ...prev,
                        [`heroImage${imageNumber}`]: ''
                    }))
                    await fetchHero()
                }
            } else if (imageType === 'about') {
                result = await Delete('about', `${aboutId}?store_id=${storeId}&delete_image=true&domain=${domain}`)
                if (result) {
                    setSuccessMessage('About image deleted successfully')
                    setContent(prev => ({
                        ...prev,
                        aboutImage: ''
                    }))
                    await fetchAbout()
                }
            } else if (imageType === 'logo') {
                result = await Patch('headers', `${headerId}?store_id=${storeId}&domain=${domain}`, { logo: null, store: storeId })
                if (result) {
                    setSuccessMessage('Logo deleted successfully')
                    setContent(prev => ({
                        ...prev,
                        logo: ''
                    }))
                    await fetchHeader()
                }
            }

            if (result) {
                setShowSuccessModal(true)
            }
        } catch (error) {
            console.error('Error deleting image:', error)
            setSuccessMessage('Failed to delete image')
            setShowSuccessModal(true)
        } finally {
            setLoading(false)
        }
    }

    const tabs = [
        { id: 'header', label: 'Header' },
        { id: 'hero', label: 'Hero Section' },
        { id: 'offerline', label: 'Offer Line' },
        { id: 'footer', label: 'Footer' },
        { id: 'Aboutus', label: 'About us' },
        { id: 'Contactus', label: 'Contact us' },
    ]

    if (loading && !storeId) return <LoadingSpinner />

    return (
        <div className="responsive-box1">
            <div className="content-management-container">
                <SuccessModal
                    show={showSuccessModal}
                    message={successMessage}
                    onClose={() => setShowSuccessModal(false)}
                />

                {/* Vertical Sidebar */}
                <div className="content-sidebar">
                    <div className="header-section" style={{ marginBottom: '20px' }}>
                        <h2 style={{ fontSize: '18px', margin: '0 0 16px 0', color: '#1e293b' }}>Content Management</h2>
                    </div>

                    <div className="content-tabs">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                className={`content-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="content-main">
                    <div className="content-sections">
                        {activeTab === 'header' && (
                            <>
                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Logo</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        {content.logo && (
                                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                                <img src={content.logo} className="logo-preview" alt="Logo" />
                                                <button
                                                    onClick={() => handleDeleteImage('logo')}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '5px',
                                                        right: '5px',
                                                        background: '#ef4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        width: '24px',
                                                        height: '24px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        fontSize: '12px'
                                                    }}
                                                    title="Delete Logo"
                                                >
                                                    <FaTrash size={10} />
                                                </button>
                                            </div>
                                        )}
                                        {content.logo && (
                                            <>
                                                <p style={{ fontSize: '12px', color: '#10b981', margin: '5px 0', fontWeight: '500' }}>✓ Currently Uploaded</p>
                                                <p style={{ fontSize: '11px', color: '#6b7280', margin: '0 0 10px 0', wordBreak: 'break-all' }}>{content.logo.split('/').pop()}</p>
                                            </>
                                        )}
                                        <div className="logo-upload">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => setLogoFile(e.target.files[0])}
                                                className="file-input"
                                            />
                                            {logoFile && <p className="file-name">Selected: {logoFile.name}</p>}
                                        </div>
                                        {logoFile && (
                                            <>
                                                <img
                                                    src={URL.createObjectURL(logoFile)}
                                                    className="logo-preview"
                                                />
                                                <p style={{ fontSize: '12px', color: '#3b82f6', margin: '5px 0', fontWeight: '500' }}>✓ New Logo Selected</p>
                                                <p style={{ fontSize: '11px', color: '#6b7280', margin: '0', wordBreak: 'break-all' }}>{logoFile.name}</p>
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Site Name</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="text"
                                            value={content.siteName}
                                            onChange={(e) => setContent({ ...content, siteName: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter site name"
                                        />
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Menu Items</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        {(content.menuItems || []).map((item, index) => (
                                            <div key={index} className="menu-item-row">
                                                <input
                                                    type="text"
                                                    placeholder="Name"
                                                    value={item.name}
                                                    onChange={(e) => updateMenuItem(index, 'name', e.target.value)}
                                                    className="text-input"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Link"
                                                    value={item.link}
                                                    onChange={(e) => updateMenuItem(index, 'link', e.target.value)}
                                                    className="text-input"
                                                />
                                                <button className="remove-btn" onClick={() => removeMenuItem(index)}>
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                        <div className="menu-actions">
                                            <button
                                                className="default-pages-btn"
                                                onClick={async () => {
                                                    const result = await confirm({
                                                        title: 'Set Default Pages?',
                                                        text: 'This will replace current menu items with Home, About Us, and Products.',
                                                        icon: 'question',
                                                        confirmButtonText: 'Yes, set defaults',
                                                        confirmButtonColor: 'rgb(74, 85, 70)',
                                                        cancelButtonColor: '#6b7280'
                                                    })
                                                    if (!result.isConfirmed) return
                                                    if (!storeId) {
                                                        handleToast({ type: 'ERROR', message: 'No store selected' })
                                                        return
                                                    }

                                                    const defaultItems = [
                                                        { name: 'Home', link: '/' },
                                                        { name: 'About Us', link: '#about' },
                                                        { name: 'Products', link: '/products' }
                                                    ]

                                                    const domain = window.location.hostname
                                                        setLoading(true)
                                                        try {
                                                            if (headerId) {
                                                            await Patch('headers', `${headerId}?store_id=${storeId}&domain=${domain}`, { menu_items: defaultItems, store: storeId })
                                                        } else {
                                                            const fd = new FormData()
                                                            fd.append('title', content.siteName || '')
                                                            fd.append('menu_items', JSON.stringify(defaultItems))
                                                            fd.append('store', storeId)
                                                            await PostFormData(`headers?store_id=${storeId}&domain=${domain}`, fd)
                                                        }
                                                        setContent(prev => ({ ...prev, menuItems: defaultItems }))
                                                        setSuccessMessage('Default pages set successfully')
                                                        setShowSuccessModal(true)
                                                        await fetchHeader()
                                                    } catch (e) {
                                                        console.error('Failed to set default pages:', e)
                                                    } finally {
                                                        setLoading(false)
                                                    }
                                                }}
                                            >
                                                Set Default Pages?
                                            </button>
                                            <CustomButton
                                                text="+ Add Menu Item"
                                                backgroundColor="rgb(74, 85, 70)"
                                                color="#fff"
                                                width="150px"
                                                height="38px"
                                                onClick={addMenuItem}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'hero' && (
                            <>
                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Hero Title</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="text"
                                            value={content.heroTitle}
                                            onChange={(e) => setContent({ ...content, heroTitle: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter hero title"
                                        />
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Price</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="text"
                                            value={content.heroSubtitle}
                                            onChange={(e) => setContent({ ...content, heroSubtitle: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter Price or Subtitle"
                                        />
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Hero Images (4 Images)</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                                            {[1, 2, 3, 4].map(num => (
                                                <div key={num} style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f9fafb', position: 'relative' }}>
                                                    <label style={{ fontSize: '14px', fontWeight: '600', color: '#282e26' }}>Hero Image {num}</label>
                                                    {content[`heroImage${num}`] && (
                                                        <>
                                                            <div style={{ position: 'relative' }}>
                                                                <img src={content[`heroImage${num}`]} style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #10b981' }} alt={`Hero ${num}`} />
                                                                <button
                                                                    onClick={() => handleDeleteImage('hero', num)}
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: '5px',
                                                                        right: '5px',
                                                                        background: '#ef4444',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '50%',
                                                                        width: '24px',
                                                                        height: '24px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        cursor: 'pointer',
                                                                        fontSize: '12px'
                                                                    }}
                                                                    title={`Delete Hero Image ${num}`}
                                                                >
                                                                    <FaTrash size={10} />
                                                                </button>
                                                            </div>
                                                            <p style={{ fontSize: '12px', color: '#10b981', margin: 0, fontWeight: '500' }}>✓ Currently Uploaded</p>
                                                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0, wordBreak: 'break-all' }}>{content[`heroImage${num}`].split('/').pop()}</p>
                                                        </>
                                                    )}
                                                    {!content[`heroImage${num}`] && (
                                                        <div style={{ width: '100%', height: '120px', borderRadius: '6px', border: '2px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '13px' }}>No image</div>
                                                    )}
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={(e) => setHeroImageFiles({ ...heroImageFiles, [`img${num}`]: e.target.files[0] })}
                                                        className="file-input"
                                                        style={{ fontSize: '13px' }}
                                                    />
                                                    {heroImageFiles[`img${num}`] && (
                                                        <>
                                                            <img src={URL.createObjectURL(heroImageFiles[`img${num}`])} style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #3b82f6' }} alt={`New ${num}`} />
                                                            <p style={{ fontSize: '12px', color: '#3b82f6', margin: 0, fontWeight: '500' }}>✓ New Image Selected</p>
                                                            <p style={{ fontSize: '11px', color: '#6b7280', margin: 0, wordBreak: 'break-all' }}>{heroImageFiles[`img${num}`].name}</p>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'offerline' && (
                            <div className="offerline-wrapper">
                                <OfferLine storeId={storeId} />
                            </div>
                        )}

                        {activeTab === 'footer' && (
                            <>
                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Company Name</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="text"
                                            value={content.companyName}
                                            onChange={(e) => setContent({ ...content, companyName: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter company name"
                                        />
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Description</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <textarea
                                            value={content.description}
                                            onChange={(e) => setContent({ ...content, description: e.target.value })}
                                            className="textarea-input"
                                            rows={4}
                                            placeholder="Enter company description"
                                        />
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Address</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="text"
                                            value={content.address}
                                            onChange={(e) => setContent({ ...content, address: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter company address"
                                        />
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Phone</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="text"
                                            value={content.phone}
                                            onChange={(e) => setContent({ ...content, phone: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter phone number"
                                        />
                                        {errors.phone && <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>{errors.phone}</p>}
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Email</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="email"
                                            value={content.email}
                                            onChange={(e) => setContent({ ...content, email: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter email address"
                                        />
                                        {errors.email && <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>{errors.email}</p>}
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Copyright Text</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="text"
                                            value={content.copyrightText}
                                            onChange={(e) => setContent({ ...content, copyrightText: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter copyright text"
                                        />
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Social Links</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        {(content.socialLinks || []).map((item, index) => (
                                            <div key={index} className="menu-item-row">
                                                <input
                                                    type="text"
                                                    placeholder="Platform"
                                                    value={item.platform}
                                                    onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                                                    className="text-input"
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="URL"
                                                    value={item.url}
                                                    onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                                                    className="text-input"
                                                />
                                                <button className="remove-btn" onClick={() => removeSocialLink(index)}>
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                        <div className="menu-actions">
                                            <CustomButton
                                                text="+ Add Social Link"
                                                backgroundColor="rgb(74, 85, 70)"
                                                color="#fff"
                                                width="150px"
                                                height="38px"
                                                onClick={addSocialLink}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'Aboutus' && (
                            <>
                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>About Title</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="text"
                                            value={content.aboutTitle}
                                            onChange={(e) => setContent({ ...content, aboutTitle: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter about title"
                                        />
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>About Description</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <textarea
                                            value={content.aboutDescription}
                                            onChange={(e) => setContent({ ...content, aboutDescription: e.target.value })}
                                            className="textarea-input"
                                            rows={4}
                                            placeholder="Enter about description"
                                        />
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>About Image</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        {content.aboutImage && (
                                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                                <img src={content.aboutImage} className="logo-preview" />
                                                <button
                                                    onClick={() => handleDeleteImage('about')}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '5px',
                                                        right: '5px',
                                                        background: '#ef4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        width: '24px',
                                                        height: '24px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        fontSize: '12px'
                                                    }}
                                                    title="Delete Image"
                                                >
                                                    <FaTrash size={10} />
                                                </button>
                                            </div>
                                        )}
                                        <div className="logo-upload">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => setLogoFile(e.target.files[0])}
                                                className="file-input"
                                            />
                                            {logoFile && <p className="file-name">Selected: {logoFile.name}</p>}
                                        </div>
                                        {logoFile && (
                                            <img
                                                src={URL.createObjectURL(logoFile)}
                                                className="logo-preview"
                                            />
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === 'Contactus' && (
                            <>
                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Phone Number</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="text"
                                            value={content.contactNumber}
                                            onChange={(e) => setContent({ ...content, contactNumber: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter phone number"
                                        />
                                        {errors.contactNumber && <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>{errors.contactNumber}</p>}
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Email</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <input
                                            type="email"
                                            value={content.contactEmail}
                                            onChange={(e) => setContent({ ...content, contactEmail: e.target.value })}
                                            className="text-input"
                                            placeholder="Enter email address"
                                        />
                                        {errors.contactEmail && <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '5px' }}>{errors.contactEmail}</p>}
                                    </div>
                                </div>

                                <div className="content-card">
                                    <div className="content-card-header">
                                        <div className="card-title">
                                            <h3>Address</h3>
                                        </div>
                                    </div>
                                    <div className="edit-section">
                                        <textarea
                                            value={content.contactAddress}
                                            onChange={(e) => setContent({ ...content, contactAddress: e.target.value })}
                                            className="textarea-input"
                                            rows={3}
                                            placeholder="Enter contact address"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Fixed Footer with Save Button */}
                    <div className="content-footer">
                        <CustomButton
                            text={loading ? "Saving..." : "Save Changes"}
                            backgroundColor={hasChanges ? "rgb(74, 85, 70)" : "#9ca3af"}
                            color="#fff"
                            width="200px"
                            height="45px"
                            onClick={handleSaveAll}
                            disabled={!hasChanges || loading}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ContentManagement
